/**
 * The work tools' people-facing routes (ADR-0091): notifications agents send to people or teams
 * (notify tool) and the files agents make (documents tool). Only these routes are reachable
 * through the edge; the tools' MCP endpoints stay internal.
 */
import type { Http } from "./http";

export interface Bilingual {
  en: string;
  ar: string;
}

export interface AgentNotification {
  id: string;
  /** `{person: name}` or `{role: name}` (a team). */
  to: { person?: string; role?: string };
  title: Bilingual;
  message: Bilingual;
  /** A console page, e.g. `/documents/d…` or `/cases/<id>`. */
  link: string | null;
  created_at: string;
  agent: string | null;
  run_id: string | null;
  read: boolean;
}

export interface MadeFile {
  document_id: string;
  name: string;
  media_type: string;
  size: number;
  data_level: "public" | "internal" | "confidential" | "restricted";
  created_at: string;
  created_by: string;
  download_path: string;
}

const enc = encodeURIComponent;

/** Only console pages may be followed from a notification (no other sites, no scripts). */
export function safeLink(link: string | null | undefined): string | null {
  return link && /^\/[A-Za-z0-9/_.?=&-]*$/.test(link) && !link.startsWith("//") ? link : null;
}

export function notifyApi(http: Http) {
  return {
    list: (opts: { unreadOnly?: boolean; limit?: number } = {}) =>
      http.get<AgentNotification[]>("notify", "/v1/notifications", { unread_only: opts.unreadOnly, limit: opts.limit }),
    unreadCount: () => http.get<{ unread: number }>("notify", "/v1/notifications/unread-count"),
    /** Mark these as read, or every notification of the person when `ids` is omitted. */
    markRead: (ids?: string[]) => http.post<{ marked: number }>("notify", "/v1/notifications/read", { ids: ids ?? null }),
  };
}

export function documentsApi(http: Http) {
  return {
    file: (id: string) => http.get<MadeFile>("documents", `/v1/files/${enc(id)}`),
    /** The file itself, fetched with the person's sign-in (never a public link). */
    content: async (id: string): Promise<Blob> => (await http.raw("documents", `/v1/files/${enc(id)}/content`)).blob(),
  };
}
